import { Component, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  UserIssueCategoriesLabels,
  UserIssueCategoriesIcons,
  ContactFormData,
  ImageFile,
  LoggedUserIssueCategories,
  UnloggedUserIssueCategories,
  UserIssueCategories,
  TransactionSummary } from 'src/app/interfaces';
import { TakePictureDialogComponent } from 'src/app/private/dialogs/take-picture-dialog/take-picture-dialog.component';
import { SvgIconId } from 'src/app/shared/icons/phosphor-icons';
import { HttpService } from 'src/app/services/http.service';

@Component({
  selector: 'app-contact-modal',
  templateUrl: './contact-modal.component.html',
  styleUrls: ['./contact-modal.component.scss']
})
export class ContactModalComponent implements OnDestroy {
  helpForm: FormGroup;
  maxImages: number = 3;
  isLogged: boolean = true;
  categories: UserIssueCategories[];
  // This is a dictionary to map categories to their labels
  categoriesDict: Record<UserIssueCategories, string> = UserIssueCategoriesLabels;
  categoryIcons: Record<UserIssueCategories, SvgIconId> = UserIssueCategoriesIcons;

  // Transaction selection properties
  recentTransactions: TransactionSummary[] = [];
  loadingTransactions = false;
  preSelectedTransaction?: TransactionSummary;
  private transactionSubscription?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ContactModalComponent>,
    @Inject(MAT_DIALOG_DATA) dialogData: { isLogged: boolean; transaction?: TransactionSummary },
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private httpService: HttpService,
    formBuilder: FormBuilder,
  ) {
    if (dialogData) {
      this.isLogged = dialogData.isLogged;
      this.preSelectedTransaction = dialogData.transaction;
    }
    this.categories = this.isLogged ?
      Object.values(LoggedUserIssueCategories).filter(category => category !== LoggedUserIssueCategories.SUGGESTIONS) :
      Object.values(UnloggedUserIssueCategories)

    // Initialize form with transaction field for logged users
    this.helpForm = formBuilder.group({
      message: [null, Validators.required],
      category: [null, [Validators.required]],
      images: [[]]
    });

    if (this.isLogged) {
      const value = this.preSelectedTransaction ? this.preSelectedTransaction : null;
      this.helpForm.addControl('transaction', formBuilder.control(value));
    }
    else {
      this.helpForm.addControl('email', formBuilder.control(null, [Validators.required, Validators.email]));
    }
  }

  ngOnDestroy(): void {
    // Clean up image previews from the form's images field
    const images = this.helpForm.get('images')?.value as ImageFile[] || [];
    for (let imageFile of images) {
      if (imageFile && imageFile.preview) {
        URL.revokeObjectURL(imageFile.preview);
      }
    }

    // Clean up transaction subscription if it exists
    if (this.transactionSubscription) {
      this.transactionSubscription.unsubscribe();
    }
  }

  openTakePictureDialog() {
    this.dialog.open(TakePictureDialogComponent, {
      maxWidth: '90vw',
      maxHeight: '80vh',
      width: '400px',
      height: '600px',
    }).afterClosed().subscribe((photo: File) => {
      if (!photo) return;
      this.addNewPhoto(photo);
    })
  }

  addNewPhoto(photo: File) {
    const currentImages = this.helpForm.get('images')?.value as ImageFile[] || [];
    if (currentImages.length >= this.maxImages) {
      this.snackBar.open(`No puedes añadir más de ${this.maxImages} imágenes.`, "Cerrar");
      return;
    }

    const newImage: ImageFile = {
      file: photo,
      preview: URL.createObjectURL(photo),
    }
    const updatedImages = [...currentImages, newImage];
    this.helpForm.get('images')?.setValue(updatedImages);
    this.helpForm.get('images')?.updateValueAndValidity();

    this.cdr.detectChanges();
    this.snackBar.open("Imagen añadida correctamente.", "Cerrar", {
      duration: 3000,
    });
  }

  removeImage(index: number): void {
    const currentImages = this.helpForm.get('images')?.value as ImageFile[] || [];

    if (index >= 0 && index < currentImages.length) {
      const removedImage = currentImages[index];

      // Revoke the object URL to free memory
      if (removedImage && removedImage.preview) {
        URL.revokeObjectURL(removedImage.preview);
      }

      // Create new array without the removed image
      const updatedImages = currentImages.filter((_, i) => i !== index);
      this.helpForm.get('images')?.setValue(updatedImages);
      this.helpForm.get('images')?.updateValueAndValidity();

      this.cdr.detectChanges();
      this.snackBar.open("Imagen eliminada correctamente.", "Cerrar", {
        duration: 3000,
      });
    }
  }

  onSendHelp() {
    // Let the form validation handle image and transaction requirements instead of manual checks
    // The form validator will ensure images are required only for BAD_PRODUCT category
    // Transaction selection is required for BAD_PRODUCT and BAD_CHARGE categories in help context
    if (this.helpForm.valid) {
      const currentImages = this.helpForm.get('images')?.value as ImageFile[] || [];
      const formData: ContactFormData = {
        message: this.helpForm.value.message,
        category: this.helpForm.value.category,
        images: currentImages.map(img => img.file)
      }

      // Add email for non-logged users
      if (!this.isLogged) {
        formData.email = this.helpForm.value.email;
      }

      // Add transaction ID if present (for transaction-specific issues)
      const transaction = this.helpForm.get('transaction')?.value;
      if (transaction) {
        formData.transactionId = transaction.id;
      }

      this.dialogRef.close(formData);
    }
    else {
      // Check for specific validation errors to show appropriate messages
      const imagesControl = this.helpForm.get('images');
      const transactionControl = this.helpForm.get('transaction');

      if (imagesControl?.hasError('required')) {
        this.snackBar.open("Debes adjuntar al menos una imagen.", "Cerrar", {
          duration: 10000,
        });
      } else if (transactionControl?.hasError('required')) {
        this.snackBar.open("Debes seleccionar la transacción relacionada con el problema.", "Cerrar", {
          duration: 10000,
        });
      }
    }
  }

  onCategoryChange(category: UserIssueCategories) {
    // Set images validation based on category: required for BAD_PRODUCT, optional for others
    // This approach uses form validators instead of manual checks for better Angular integration
    if (category === LoggedUserIssueCategories.BAD_PRODUCT) {
      this.helpForm.get('images')?.setValidators([this.imagesRequiredValidator]);
    } else {
      this.helpForm.get('images')?.clearValidators();
    }
    this.helpForm.get('images')?.updateValueAndValidity();

    // Handle transaction selection for categories that require it
    if (this.isLogged && this.requiresTransactionSelection(category)) {
      this.updateTransactionValidation(category);
    } else {
      // Clear transaction validation for categories that don't require it
      const transactionControl = this.helpForm.get('transaction');
      transactionControl?.clearValidators();
      transactionControl?.updateValueAndValidity();
    }

    // Update the category in the form
    this.helpForm.get('category')?.setValue(category);
    this.cdr.detectChanges();
  }

  /**
   * Determines if the given category requires transaction selection
   * @param category The user issue category
   * @returns true if transaction selection is required for this category
   */
  private requiresTransactionSelection(category: UserIssueCategories): boolean {
    return [
      LoggedUserIssueCategories.BAD_PRODUCT,
      LoggedUserIssueCategories.BAD_CHARGE
    ].includes(category as LoggedUserIssueCategories);
  }

  /**
   * Updates transaction validation based on category selection
   * Only loads transactions if not in history context (no pre-selected transaction)
   * @param category The selected category
   */
  private updateTransactionValidation(category: UserIssueCategories): void {
    const transactionControl = this.helpForm.get('transaction');
    const needsTransaction = this.requiresTransactionSelection(category);

    if (this.preSelectedTransaction) {
      transactionControl.disable();
    }

    if (needsTransaction && !this.preSelectedTransaction) {
      // Only require transaction selection in help context (no pre-selected transaction)
      transactionControl?.setValidators([Validators.required]);
      transactionControl?.disable();
      this.loadRecentTransactions().then(() => {
        transactionControl?.enable();
      });
    } else {
      transactionControl?.clearValidators();
    }
    transactionControl?.updateValueAndValidity();
  }

  /**
   * Loads recent transactions for transaction selection
   * Only called in help context when user needs to select a transaction
   */
  private async loadRecentTransactions(): Promise<void> {
    if (this.loadingTransactions || this.recentTransactions.length > 0) {
      return; // Avoid loading multiple times
    }
    this.loadingTransactions = true;
    try {
      // Load last 10 transactions using existing HTTP service
      const response = await this.httpService.getTransactionHistory();
      if (response.successful) {
        this.recentTransactions = response.data.slice(0, 10); // Limit to 10 transactions
      }
      else {
        console.error('Error loading recent transactions:', response.message);
      }
    }
    catch (error) {
      console.error('Error loading recent transactions:', error);
    }
    finally {
      this.loadingTransactions = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Navigates to history section when user can't find their transaction
   */
  navigateToHistory(): void {
    this.dialogRef.close();
    this.router.navigate(['/history']); // Adjust route as needed
  }

  /**
   * Formats transaction for display in selection dropdown
   * @param transaction The transaction to format
   * @returns Formatted display string
   */
  formatTransactionOption(transaction: TransactionSummary): string {
    const date = new Date(transaction.requestTimestamp*1000).toLocaleDateString();
    const amount = transaction.amount ? `$${transaction.amount}` : 'N/A';
    const storeName = transaction.storeName || 'Store';
    return `${date} - ${storeName} - ${amount}`;
  }

  /**
   * Determines if transaction selector should be shown based on context and category
   * @returns true if transaction selector should be displayed
   */
  get shouldShowTransactionSelector(): boolean {
    if (!this.isLogged) return false;

    const category = this.helpForm.get('category')?.value;
    if (!category || !this.requiresTransactionSelection(category)) return false;

    return true;
  }


  // Custom validator to check if at least one image is provided
  private imagesRequiredValidator = (control: any) => {
    const images = control.value as ImageFile[] || [];
    return images.length > 0 ? null : { required: true };
  }

  // Getter for template to access images from the form
  get images(): ImageFile[] {
    return this.helpForm.get('images')?.value as ImageFile[] || [];
  }

  // Helper method to determine if images are required for the currently selected category.
  // Mirrors the condition in onCategoryChange() directly, rather than reading it back off
  // the FormControl's validator state.
  get areImagesRequired(): boolean {
    return this.helpForm.get('category')?.value === LoggedUserIssueCategories.BAD_PRODUCT;
  }

}
